import React, { useState } from 'react';
import { Card, Button, Row, Col, Space, message, Divider } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import OCAssessmentDashboard from './shared/OCAssessmentDashboard';
import AssessmentResultCard from './shared/AssessmentResultCard';
import RadarChart from './shared/RadarChart';
import { 
  SAMPLE_ASSESSMENT_DATA,
  READING_DIMENSIONS,
  THINKING_DIMENSIONS,
  MATHEMATICAL_DIMENSIONS,
  extractDimensionScores
} from '../utils/assessmentUtils';

interface AssessmentDemoProps {
  onBack: () => void;
}

const AssessmentDemo: React.FC<AssessmentDemoProps> = ({ onBack }) => {
  const [currentView, setCurrentView] = useState<'dashboard' | 'individual' | 'comparison'>('dashboard');

  const handleDetailView = (module: 'reading' | 'thinking' | 'mathematics') => {
    message.info(`查看 ${module} 详细分析`);
  };

  const handleRetakeTest = (module: 'reading' | 'thinking' | 'mathematics') => {
    message.info(`重新测试 ${module}`);
  };

  const renderDashboardView = () => (
    <OCAssessmentDashboard
      assessmentData={SAMPLE_ASSESSMENT_DATA}
      onDetailView={handleDetailView}
      onRetakeTest={handleRetakeTest}
    />
  );

  const renderIndividualView = () => (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ textAlign: 'center', marginBottom: '32px' }}>Individual Assessment Cards</h2>
        
        <Row gutter={24}>
          <Col span={8}>
            <AssessmentResultCard
              type="reading"
              data={SAMPLE_ASSESSMENT_DATA.reading}
              overallScore={SAMPLE_ASSESSMENT_DATA.reading.overallScore}
              onViewDetails={() => handleDetailView('reading')}
              onRetakeTest={() => handleRetakeTest('reading')}
            />
          </Col>
          
          <Col span={8}>
            <AssessmentResultCard
              type="thinking"
              data={SAMPLE_ASSESSMENT_DATA.thinking}
              overallScore={SAMPLE_ASSESSMENT_DATA.thinking.overallScore}
              onViewDetails={() => handleDetailView('thinking')}
              onRetakeTest={() => handleRetakeTest('thinking')}
            />
          </Col>
          
          <Col span={8}>
            <AssessmentResultCard
              type="mathematics"
              data={SAMPLE_ASSESSMENT_DATA.mathematics}
              overallScore={SAMPLE_ASSESSMENT_DATA.mathematics.overallScore}
              onViewDetails={() => handleDetailView('mathematics')}
              onRetakeTest={() => handleRetakeTest('mathematics')}
            />
          </Col>
        </Row>

        <Divider style={{ margin: '48px 0' }} />

        <h3 style={{ textAlign: 'center', marginBottom: '32px' }}>Compact View (No Radar Charts)</h3>
        
        <Row gutter={24}>
          <Col span={8}>
            <AssessmentResultCard
              type="reading"
              data={SAMPLE_ASSESSMENT_DATA.reading}
              overallScore={SAMPLE_ASSESSMENT_DATA.reading.overallScore}
              showRadarChart={false}
              size="small"
            />
          </Col>
          
          <Col span={8}>
            <AssessmentResultCard
              type="thinking"
              data={SAMPLE_ASSESSMENT_DATA.thinking}
              overallScore={SAMPLE_ASSESSMENT_DATA.thinking.overallScore}
              showRadarChart={false}
              size="small"
            />
          </Col>
          
          <Col span={8}>
            <AssessmentResultCard
              type="mathematics"
              data={SAMPLE_ASSESSMENT_DATA.mathematics}
              overallScore={SAMPLE_ASSESSMENT_DATA.mathematics.overallScore}
              showRadarChart={false}
              size="small"
            />
          </Col>
        </Row>
      </div>
    </div>
  );

  const renderComparisonView = () => (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <h2 style={{ textAlign: 'center', marginBottom: '32px' }}>Radar Chart Comparison</h2>
      
      <Row gutter={24}>
        <Col span={8}>
          <Card title="Reading Comprehension" style={{ height: '500px' }}>
            <RadarChart
              title=""
              data={extractDimensionScores(SAMPLE_ASSESSMENT_DATA.reading, READING_DIMENSIONS)}
              dimensions={READING_DIMENSIONS}
              size="small"
              onDimensionClick={(dimension) => message.info(`点击了: ${dimension.label}`)}
            />
          </Card>
        </Col>
        
        <Col span={8}>
          <Card title="Thinking Skills" style={{ height: '500px' }}>
            <RadarChart
              title=""
              data={extractDimensionScores(SAMPLE_ASSESSMENT_DATA.thinking, THINKING_DIMENSIONS)}
              dimensions={THINKING_DIMENSIONS}
              size="small"
              onDimensionClick={(dimension) => message.info(`点击了: ${dimension.label}`)}
            />
          </Card>
        </Col>
        
        <Col span={8}>
          <Card title="Mathematical Reasoning" style={{ height: '500px' }}>
            <RadarChart
              title=""
              data={extractDimensionScores(SAMPLE_ASSESSMENT_DATA.mathematics, MATHEMATICAL_DIMENSIONS)}
              dimensions={MATHEMATICAL_DIMENSIONS}
              size="small"
              onDimensionClick={(dimension) => message.info(`点击了: ${dimension.label}`)}
            />
          </Card>
        </Col>
      </Row>

      <Divider style={{ margin: '48px 0' }} />

      <h3 style={{ textAlign: 'center', marginBottom: '32px' }}>Large Size Radar Charts</h3>
      
      <Row gutter={48} justify="center">
        <Col span={12}>
          <Card title="Reading vs Thinking Skills">
            <div style={{ display: 'flex', gap: '24px' }}>
              <div style={{ flex: 1 }}>
                <RadarChart
                  title="Reading"
                  data={extractDimensionScores(SAMPLE_ASSESSMENT_DATA.reading, READING_DIMENSIONS)}
                  dimensions={READING_DIMENSIONS}
                  size="small"
                  showLegend={false}
                />
              </div>
              <div style={{ flex: 1 }}>
                <RadarChart
                  title="Thinking"
                  data={extractDimensionScores(SAMPLE_ASSESSMENT_DATA.thinking, THINKING_DIMENSIONS)}
                  dimensions={THINKING_DIMENSIONS}
                  size="small"
                  showLegend={false}
                />
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );

  return (
    <div>
      {/* Navigation */}
      <div style={{ 
        padding: '16px 24px', 
        background: 'white', 
        borderBottom: '1px solid #f0f0f0',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={onBack}
          >
            Back to Dashboard
          </Button>
          
          <Divider type="vertical" />
          
          <Button 
            type={currentView === 'dashboard' ? 'primary' : 'default'}
            onClick={() => setCurrentView('dashboard')}
          >
            Complete Dashboard
          </Button>
          
          <Button 
            type={currentView === 'individual' ? 'primary' : 'default'}
            onClick={() => setCurrentView('individual')}
          >
            Individual Cards
          </Button>
          
          <Button 
            type={currentView === 'comparison' ? 'primary' : 'default'}
            onClick={() => setCurrentView('comparison')}
          >
            Radar Chart Demo
          </Button>
        </Space>
      </div>

      {/* Content */}
      {currentView === 'dashboard' && renderDashboardView()}
      {currentView === 'individual' && renderIndividualView()}
      {currentView === 'comparison' && renderComparisonView()}
    </div>
  );
};

export default AssessmentDemo;